import { useCallback, useEffect, useRef } from 'react';

import {
  useBackerHasStudentFeesAccess,
  usePrefetchConsumerBackerActivity,
  usePrefetchConsumerBackerIntent,
} from '@givebacks/api/backer';
import { useListUserCauseSelections } from '@givebacks/api/core';
import { useNavigate } from 'react-router-dom';
import {
  ButtonMantine,
  Card,
  Carousel,
  CarouselAutoplay,
  ImageMantine,
  Stack,
  Text,
  ThemeIcon,
} from 'ui';
import { IconChevronLeft, IconChevronRight } from 'ui/Icons';

import { OnboardingModal } from '../OnboardingModal';
import classes from './Welcome.module.css';

const PAGE_IMAGES = [
  {
    imageSource:
      'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/onboarding/Store.png',
    headline: 'Buy event tickets and store items',
  },
  {
    imageSource:
      'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/onboarding/Membership.png',
    headline: 'Buy a membership',
  },
  {
    imageSource:
      'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/onboarding/Fundraiser.png',
    headline: 'Support a fundraiser',
  },
  {
    imageSource:
      'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/onboarding/Fees.png',
    headline: 'Pay student fees',
  },
  {
    imageSource:
      'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/onboarding/STG.png',
    headline: 'Earn with Rewards',
  },
];

const WelcomeContent = () => {
  const navigate = useNavigate();
  const autoplay = useRef(CarouselAutoplay({ delay: 3000, stopOnInteraction: true }));
  // If the user has student fees access, we want to redirect to the onboarding your connected screen for student fees
  const { hasStudentFeesAccess, isLoading } = useBackerHasStudentFeesAccess();
  const { data: userCauseSelections, isLoading: isUserCauseSelectionsLoading } =
    useListUserCauseSelections();
  const prefetchConsumerBackerIntent = usePrefetchConsumerBackerIntent();
  const prefetchConsumerBackerActivity = usePrefetchConsumerBackerActivity();

  useEffect(() => {
    // Prefetch data to avoid loading state when user navigates to causes screen
    void prefetchConsumerBackerIntent();
    void prefetchConsumerBackerActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  const onFeeAccessContinue = useCallback(() => {
    if (userCauseSelections && userCauseSelections.length > 0) {
      navigate('/onboarding/causes-following');
    } else {
      navigate('/onboarding/causes');
    }
  }, [userCauseSelections, navigate]);

  /**
   * Handles the next steps for onboarding
   * Users with student fees access will be redirected to the you are connected screen
   * Users without student fees access will be redirected to the onboarding intent screen
   */
  const handleGetStarted = useCallback(() => {
    if (hasStudentFeesAccess) {
      onFeeAccessContinue();
    } else {
      navigate('/onboarding/intent');
    }
  }, [hasStudentFeesAccess, onFeeAccessContinue, navigate]);

  return (
    <Card w="100%">
      <Stack gap={0} ta="center">
        <Text fw="500" fz="14" c="gray.6" mt="md">
          With Givebacks you can,
        </Text>
        <Stack w="100%" mb="24px">
          <Carousel
            slideSize="100%"
            slideGap={0}
            align="center"
            withControls
            withIndicators
            nextControlIcon={
              <ThemeIcon variant="transparent" c="gray.7">
                <IconChevronRight />
              </ThemeIcon>
            }
            previousControlIcon={
              <ThemeIcon variant="transparent" c="gray.7">
                <IconChevronLeft />
              </ThemeIcon>
            }
            loop
            plugins={[autoplay.current]}
            classNames={{
              indicators: classes.indicators,
              indicator: classes.indicator,
              control: classes.control,
              container: classes.container,
            }}
            type="container"
          >
            {PAGE_IMAGES.map((page, index) => (
              <Carousel.Slide key={index}>
                <Card key={index}>
                  <Stack justify="center" align="center">
                    <Text fw="600" fz="20">
                      {page.headline}
                    </Text>
                    <ImageMantine src={page.imageSource} p="0 6rem" />
                  </Stack>
                </Card>
              </Carousel.Slide>
            ))}
          </Carousel>
        </Stack>
        <ButtonMantine
          size="lg"
          onClick={handleGetStarted}
          mb="16px"
          loading={isLoading || isUserCauseSelectionsLoading}
        >
          Get Started
        </ButtonMantine>
      </Stack>
    </Card>
  );
};

const Welcome = () => {
  return (
    <OnboardingModal
      title="Welcome to Givebacks"
      description="Everything you need to support schools and nonprofits."
    >
      <WelcomeContent />
    </OnboardingModal>
  );
};

export { Welcome };
